/**
 * Règles, without React: how a rule's value reads in its unit, the input a
 * new version takes for each value type, what must hold before it is sent,
 * and the request. The server checks all of it again (company-rules.ts).
 */
import type { StaffFeatureId } from "@wallyo/contracts/actions";
import type {
  CreateRuleVersionRequest,
  RuleListItem,
  RuleValue,
  RuleVersionItem,
} from "@wallyo/contracts/console/configuration";
import { VALUE_TYPES, type Unit, type ValueType, type ValueTypeName } from "@wallyo/contracts/settings";
import type { ConsoleClient } from "../../api/client";
import { createRuleVersion } from "../../api/console-api";
import { t, type ConsoleLanguage, type MessageId } from "../../i18n";
import { formatInUnit, formatNumber } from "../../i18n/format";
import { fromLocalInput } from "../local-datetime";
import { isOwner } from "../team/team-access";
import { REASON_MIN_LENGTH } from "../team/team-writes";

export const EDIT_RULES: StaffFeatureId = "EDIT_RULES_STAFF_FEATURE_ENABLEMENT";

// ── The input for each value type ────────────────────────────────────────

/**
 * - `gnf`: a whole amount in Guinean francs, 0 or more;
 * - `duration`: a whole number of minutes, hours or days, 1 or more;
 * - `number`: any other number, within its bounds;
 * - `boolean`, `choice` (an enum) and `text`.
 */
export type RuleInput =
  | { kind: "gnf"; min: number }
  | { kind: "duration"; unit: "minutes" | "hours" | "days"; min: number }
  | { kind: "number"; unit: Unit | null; min: number | null; max: number | null; integer: boolean }
  | { kind: "boolean" }
  | { kind: "choice"; options: readonly string[] }
  | { kind: "text"; maxLength: number | null };

export function ruleInput(valueType: ValueTypeName): RuleInput {
  const type = VALUE_TYPES[valueType] as ValueType;
  switch (type.type) {
    case "boolean":
      return { kind: "boolean" };
    case "enum":
      return { kind: "choice", options: type.values };
    case "string":
      return { kind: "text", maxLength: type.maxLength ?? null };
    case "number":
      if (type.unit === "GNF") return { kind: "gnf", min: type.min ?? 0 };
      if ((type.unit === "minutes" || type.unit === "hours" || type.unit === "days") && type.integer) {
        return { kind: "duration", unit: type.unit, min: type.min ?? 1 };
      }
      return {
        kind: "number",
        unit: type.unit ?? null,
        min: type.min ?? null,
        max: type.max ?? null,
        integer: type.integer === true,
      };
  }
}

/** What the value is, e.g. "montant en GNF", "durée en heures" (the detail's subtitle). */
export function valueKindLabel(input: RuleInput): string {
  switch (input.kind) {
    case "gnf":
      return t("rules.kind.gnf");
    case "duration":
      return t(DURATION_KIND[input.unit]);
    case "number":
      return input.unit === "km" ? t("rules.kind.km") : t("rules.kind.number");
    case "boolean":
      return t("rules.kind.boolean");
    case "choice":
      return t("rules.kind.choice");
    case "text":
      return t("rules.kind.text");
  }
}

const DURATION_KIND: Record<"minutes" | "hours" | "days", MessageId> = {
  minutes: "rules.kind.minutes",
  hours: "rules.kind.hours",
  days: "rules.kind.days",
};

const VALUE_LABEL: Record<RuleInput["kind"], MessageId> = {
  gnf: "rules.form.value.gnf",
  duration: "rules.form.value",
  number: "rules.form.value",
  boolean: "rules.form.value",
  choice: "rules.form.value",
  text: "rules.form.value",
};

const DURATION_LABEL: Record<"minutes" | "hours" | "days", MessageId> = {
  minutes: "rules.form.value.minutes",
  hours: "rules.form.value.hours",
  days: "rules.form.value.days",
};

/** The value field's label: "Nouveau montant (GNF)", "Nouvelle valeur (en heures)"… */
export const valueInputLabel = (input: RuleInput): string =>
  t(input.kind === "duration" ? DURATION_LABEL[input.unit] : VALUE_LABEL[input.kind]);

// ── How a value reads ────────────────────────────────────────────────────

/**
 * A rule's value in its unit and the staff member's language: "1 000 000 GNF"
 * (fr), "GNF 1,000,000" (en), "2 heures", "Oui".
 */
export function formatRuleValue(
  rule: Pick<RuleListItem, "valueType">,
  value: RuleValue,
  language?: ConsoleLanguage,
): string {
  if (typeof value === "boolean") return t(value ? "rules.value.true" : "rules.value.false");
  if (typeof value === "string") return value;
  const input = ruleInput(rule.valueType);
  switch (input.kind) {
    case "gnf":
      return formatInUnit(value, "GNF", language);
    case "duration":
      return formatInUnit(value, input.unit, language);
    case "number":
      return input.unit ? formatInUnit(value, input.unit, language) : formatNumber(value, undefined, language);
    default:
      return String(value);
  }
}

export type VersionState = "scheduled" | "current" | "past";

/** A version is scheduled until it takes effect, current while it applies, past once replaced. */
export function versionState(rule: RuleListItem, version: RuleVersionItem, now: Date): VersionState {
  if (new Date(version.effectiveFrom).getTime() > now.getTime()) return "scheduled";
  if (rule.current.source === "version" && rule.current.ruleVersion.ruleVersionId === version.ruleVersionId) {
    return "current";
  }
  return "past";
}

/** Why this person can't add a version, or null when they can. The server decides again. */
export function ruleLock(
  rule: Pick<RuleListItem, "sensitivity" | "status">,
  features: readonly StaffFeatureId[],
): "retired" | "critical" | null {
  if (rule.status === "retired") return "retired";
  return rule.sensitivity === "critical" && !isOwner(features) ? "critical" : null;
}

// ── The form ─────────────────────────────────────────────────────────────

export type RuleVersionFields = {
  /** As typed; a boolean is "true" or "false". */
  value: string;
  effective: "now" | "at";
  /** A `datetime-local` value, used when `effective` is "at". */
  effectiveAt: string;
  reason: string;
  legalBasis: string;
};

/** The form starts from the value that applies now. */
export function emptyVersionFields(rule: RuleListItem): RuleVersionFields {
  return { value: String(rule.current.value), effective: "now", effectiveAt: "", reason: "", legalBasis: "" };
}

export type Parsed = { ok: true; value: RuleValue } | { ok: false; problem: string };

/** The typed value, or what is wrong with it. Spaces and a thousands separator are allowed in numbers. */
export function parseRuleValue(input: RuleInput, raw: string): Parsed {
  const text = raw.trim();
  switch (input.kind) {
    case "boolean":
      return text === "true" || text === "false"
        ? { ok: true, value: text === "true" }
        : { ok: false, problem: t("rules.problem.choice") };
    case "choice":
      return input.options.includes(text)
        ? { ok: true, value: text }
        : { ok: false, problem: t("rules.problem.choice") };
    case "text":
      if (!text) return { ok: false, problem: t("rules.problem.value") };
      return input.maxLength !== null && text.length > input.maxLength
        ? { ok: false, problem: t("rules.problem.tooLong", { max: input.maxLength }) }
        : { ok: true, value: text };
    default: {
      if (!text) return { ok: false, problem: t("rules.problem.value") };
      const compact = text.replace(/[\s  ,.'’](?=\d{3}(\D|$))/g, "");
      if (!/^-?\d+(\.\d+)?$/.test(compact)) return { ok: false, problem: t("rules.problem.number") };
      const value = Number(compact);
      const integer = input.kind !== "number" || input.integer;
      if (integer && !Number.isInteger(value)) return { ok: false, problem: t("rules.problem.integer") };
      const min = input.min;
      if (min !== null && value < min) {
        return { ok: false, problem: t("rules.problem.min", { min: formatNumber(min) }) };
      }
      if (input.kind === "number" && input.max !== null && value > input.max) {
        return { ok: false, problem: t("rules.problem.max", { max: formatNumber(input.max) }) };
      }
      return { ok: true, value };
    }
  }
}

/**
 * When the version takes effect: now, or a date and time that is not in the
 * past. The server allows RULE_EFFECTIVE_FROM_GRACE_SECONDS for a slow
 * clock; the form doesn't lean on it, and "now" is left to the server.
 */
export function effectiveProblem(
  fields: Pick<RuleVersionFields, "effective" | "effectiveAt">,
  now: Date,
): string | null {
  if (fields.effective === "now") return null;
  const at = fromLocalInput(fields.effectiveAt);
  if (!at) return t("rules.problem.effectiveMissing");
  return at.getTime() <= now.getTime() ? t("rules.problem.effectivePast") : null;
}

/** Everything the form needs before it is sent, in the order the fields read; null when it can go. */
export function ruleVersionProblem(rule: RuleListItem, fields: RuleVersionFields, now: Date): string | null {
  const parsed = parseRuleValue(ruleInput(rule.valueType), fields.value);
  if (!parsed.ok) return parsed.problem;
  const effective = effectiveProblem(fields, now);
  if (effective) return effective;
  if (fields.reason.trim().length < REASON_MIN_LENGTH) return t("rules.problem.reason");
  if (rule.requiresLegalBasis && !fields.legalBasis.trim()) return t("rules.problem.legalBasis");
  return null;
}

/** The POST body, at the rule's version on screen. "Now" leaves `effectiveFrom` out: the server's now. */
export function versionRequest(rule: RuleListItem, fields: RuleVersionFields): CreateRuleVersionRequest {
  const parsed = parseRuleValue(ruleInput(rule.valueType), fields.value);
  if (!parsed.ok) throw new Error("versionRequest needs a valid value: check ruleVersionProblem first");
  const at = fields.effective === "at" ? fromLocalInput(fields.effectiveAt) : null;
  const legalBasis = fields.legalBasis.trim();
  return {
    value: parsed.value,
    ...(at ? { effectiveFrom: at.toISOString() } : {}),
    reason: fields.reason.trim(),
    legalBasis: legalBasis || null,
    version: rule.version,
  };
}

export function sendRuleVersion(
  client: ConsoleClient,
  rule: RuleListItem,
  request: CreateRuleVersionRequest,
  idempotencyKey: string,
) {
  return createRuleVersion(client, rule.ruleId, request, { idempotencyKey });
}
